import { FC, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row, Select, Typography } from 'antd'
import { useFormikContext } from 'formik'

import { DateField } from '@shared/modules/form/fields/date'
import { SelectField } from '@shared/modules/form/fields/select'
import { TextAreaField } from '@shared/modules/form/fields/textarea'

import { AppointmentFormData } from '../types'
import { useDoctorList } from '../../doctors/hooks/use-list'
import { Constants } from '@shared/constants'
import { TextField } from '@shared/modules/form/fields/text'
import { useInsuranceList } from '../../insurances/hooks/use-list'

export const AppointmentFinalForm: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { values, handleSubmit, isSubmitting, setFieldValue, touched } = useFormikContext<AppointmentFormData>()
  const [modalIsVisible, setModalIsVisible] = useState(false)
  const doctors = useDoctorList()
  const insurances = useInsuranceList()
  const touchedRef = useRef(touched)
  useImperativeHandle(touchedRef, () => touched)

  const doctorOptions = useMemo(
    () =>
      doctors.data.map((doctor) => (
        <Select.Option key={doctor.id} value={doctor.id.toString()}>
          {doctor.firstname} {doctor.lastname} {doctor.clinic && `- ${doctor.clinic.name}`}
        </Select.Option>
      )),
    [doctors.data],
  )

  const insurancesOptions = useMemo(() => {
    return insurances.data
      .filter((elem) => +elem.stateId === 16)
      .map((elem) => (
        <Select.Option value={elem.id.toString()} key={elem.id}>
          {elem.name}
        </Select.Option>
      ))
  }, [insurances.data])

  useEffect(() => {
    if (Object.values(touchedRef.current).includes(true)) {
      const totalPrice = parseFloat(values.servicesTotalPrice)
      setFieldValue('payment.cash', totalPrice.toString())
      setFieldValue('payment.bank', '0')
      setFieldValue('payment.deposit', '0')
      setFieldValue('payment.bonus', '0')
    }
  }, [values.servicesTotalPrice, setFieldValue])

  const paymentAmount = useMemo(() => parseFloat(values.servicesTotalPrice), [values.servicesTotalPrice])

  const shownFranchiseValue = useMemo(() => {
    if (paymentAmount) {
      if (values.insuranceId) {
        const franchiseStr = localStorage.getItem('franchise_value')
        const franchise = !!franchiseStr ? +franchiseStr : 100
        const result = paymentAmount * (franchise / 100)
        return parseFloat(result.toFixed(2))
      } else {
        return paymentAmount
      }
    }
    return 0
  }, [paymentAmount, values.insuranceId])

  const insurancePayment = useMemo(() => {
    if (paymentAmount && values.insuranceId && insurances.data) {
      const insurance = insurances.data.find((elem) => elem.id.toString() === values.insuranceId)
      const remaining = paymentAmount - shownFranchiseValue
      const insurancePercent = +insurance?.percent! / 100
      return remaining - remaining * insurancePercent
    }
  }, [shownFranchiseValue, insurances.data, paymentAmount, values.insuranceId])

  useEffect(() => {
    setFieldValue('payment.cash', shownFranchiseValue.toString())
  }, [shownFranchiseValue, setFieldValue])

  const enteredPaymentAmount = useMemo(
    () =>
      (parseFloat(values.payment.cash) || 0) +
      (parseFloat(values.payment.deposit) || 0) +
      (parseFloat(values.payment.bonus) || 0) +
      (parseFloat(values.payment.card) || 0),
    [values.payment.bonus, values.payment.card, values.payment.cash, values.payment.deposit],
  )

  useEffect(() => {
    const bcd = (parseFloat(values.payment.deposit) || 0) + (parseFloat(values.payment.bonus) || 0) + (parseFloat(values.payment.card) || 0)
    if (bcd <= shownFranchiseValue) {
      let result = shownFranchiseValue - bcd > 0 ? shownFranchiseValue - bcd : 0
      setFieldValue('payment.cash', result.toString())
    }
  }, [paymentAmount, setFieldValue, values.payment.bonus, values.payment.card, values.payment.deposit, shownFranchiseValue])

  return (
    <div>
      <Button disabled={disabled} onClick={() => setModalIsVisible(true)} style={{ marginTop: 12 }} type='primary' block={true}>
        Növbəti
      </Button>
      <Modal
        okButtonProps={{ disabled: enteredPaymentAmount > paymentAmount }}
        title='Əməliyyatı bitir'
        open={modalIsVisible}
        onOk={() => handleSubmit()}
        confirmLoading={isSubmitting}
        onCancel={() => setModalIsVisible(false)}
      >
        <Form layout='vertical'>
          <DateField name='scheduledAt' item={{ label: 'Qəbul tarixi' }} input={{ format: Constants.DATE_TIME }} />
          <SelectField name='type' item={{ label: 'Qəbul növü' }}>
            <Select.Option value='outpatient'>Ambulator</Select.Option>
            <Select.Option value='sent'>Göndəriş</Select.Option>
          </SelectField>
          {values.type === 'sent' && (
            <SelectField name='doctorId' item={{ label: 'Həkim' }}>
              {doctorOptions}
            </SelectField>
          )}
          <Row gutter={[16, 0]}>
            <Col xs={6}>
              <TextField name='payment.cash' format='decimal' item={{ label: 'Nağd' }} input={{ readOnly: true }} />
            </Col>
            <Col xs={6}>
              <TextField name='payment.card' format='decimal' item={{ label: 'Bank' }} />
            </Col>
            <Col xs={6}>
              <TextField name='payment.bonus' format='decimal' item={{ label: 'Bonus' }} />
            </Col>
            <Col xs={6}>
              <TextField name='payment.deposit' format='decimal' item={{ label: 'Depozit' }} />
            </Col>
            <Col xs={12}>
              <SelectField name='insuranceId' item={{ label: 'Sığorta şirkəti' }} input={{ placeholder: 'Sığorta şirkətini daxil edin...' }}>
                {insurancesOptions}
              </SelectField>
            </Col>
            <Col
              xs={12}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 13,
              }}
            >
              {values.insuranceId && <span>Sığorta ödəyəcək: {insurancePayment} &#8380;</span>}
            </Col>
          </Row>
          <Typography.Paragraph>{enteredPaymentAmount > paymentAmount && 'Daxil edilən məbləğ ümumi məbləğdən çox ola bilməz.'}</Typography.Paragraph>
          <TextAreaField name='description' item={{ label: 'Şərh' }} />
        </Form>
        <div style={{ textAlign: 'right', marginBottom: -22 }}>
          <Typography.Text>
            <b>Ümumi qiymət: </b> {shownFranchiseValue} &#8380;
          </Typography.Text>
        </div>
      </Modal>
    </div>
  )
}
